import { OrgId } from '@business/entities/organization';
import {
  MBMarketingChecklistId,
  RecurringChecklist,
  RecurringChecklistSectionId,
  RecurringChecklistSection,
  RecurringChecklistSectionStep,
  RecurringChecklistSectionStepId,
  SetupChecklistSectionId,
  SetupChecklistSectionStep,
  SetupChecklistSectionStepId,
  SetupChecklistSection,
  RecurringChecklistId,
  MBItemChecklists,
  NotOnBoardResponse,
} from './../../entities/marketing_v2/MarketingBoardResponse';
import { MarketingBoardResponse, MarketingBoardId, AttachChecklistLineItemRequest } from '@business/entities/marketing_v2';
import { Endpoint } from '@business/api/endpoint';
import { PageQuery, Paginate } from '@business/entities/common';
import { MarketingBoardFormData } from '@modules/MarketingV2/AddMarketingBoardModal/AddMarketingBoardModal';
import { ChecklistFormData } from '@modules/MarketingV2/AddChecklistModal/AddChecklistModal';
import { CompactUserResponse, UserId } from '@business/entities/account';
import { R } from '@core/utils/r';

/** Marketing Boards V2 */
export function fetchMarketingBoardItem(id: MarketingBoardId) {
  return Endpoint.get<MarketingBoardResponse>(`@gc/marketing-board-v2/board/${id}`);
}
export function fetchCompactMarketingBoardItem(id: MarketingBoardId) {
  return Endpoint.get<MarketingBoardResponse>(`@gc/marketing-board-v2/compact-board/${id}`);
}
export function fetchMarketingBoards(params: PageQuery) {
  return Endpoint.get<Paginate<MarketingBoardResponse>>(`@gc/marketing-board-v2`, { params: R.objectCompact(params) });
}
export function createMarketingBoardItem(data: MarketingBoardFormData) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2`, { data });
}
export function updateMarketingBoardItem(id: MarketingBoardId, data: Omit<MarketingBoardFormData, 'orgId' | 'combineRecord' | 'checklist'>) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/${id}`, { data });
}
export function deleteMarketingBoardItem(id: MarketingBoardId) {
  return Endpoint.delete<void>(`@gc/marketing-board-v2/${id}`);
}
export function getMarketingBoardItemChecklist(id: MBMarketingChecklistId) {
  return Endpoint.get<MBItemChecklists>(`@gc/marketing-board-v2/checklist/${id}`);
}
export function createMarketingBoardItemChecklist(data: ChecklistFormData) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/checklist`, { data });
}
export function updateMarketingBoardItemChecklist(id: MBMarketingChecklistId, data: ChecklistFormData) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/checklist/${id}`, { data });
}

export function attachLineItem(id: MBMarketingChecklistId, data: AttachChecklistLineItemRequest) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/checklist-line-item/${id}`, { data });
}

export function deleteMarketingBoardItemChecklist(id: MBMarketingChecklistId, boardId: MarketingBoardId) {
  return Endpoint.delete<MarketingBoardResponse>(`@gc/marketing-board-v2/checklist/${id}`, { data: { boardId } });
}

export function updateManager(boardId: MarketingBoardId, manager: UserId) {
  return Endpoint.patch<CompactUserResponse>(`@gc/marketing-board-v2/update-manager/${boardId}`, { data: { manager } });
}

// recurring section
export function createRecurringChecklistSection(checklistId: MBMarketingChecklistId, boardId: MarketingBoardId, data: Partial<RecurringChecklist>) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist-section`, { data: { ...data, checklistId, boardId } });
}

export function updateRecurringChecklistSection(
  id: RecurringChecklistSectionId,
  boardId: MarketingBoardId,
  data: Partial<RecurringChecklistSection>,
) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist-section/${id}`, { data: { ...data, boardId } });
}

export function updateRecurringChecklist(id: RecurringChecklistId, boardId: MarketingBoardId, feedback: string, data: Partial<RecurringChecklist>) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist/${id}`, { data: { ...data, boardId, feedback } });
}

export function deleteRecurringChecklistSection(id: MarketingBoardId, boardId: MarketingBoardId) {
  return Endpoint.delete<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist-section/${id}`, { data: { boardId } });
}

// recurring section step
export function createRecurringChecklistSectionStep(
  sectionId: RecurringChecklistSectionId,
  boardId: MarketingBoardId,
  data: Partial<RecurringChecklistSectionStep>,
) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist-step`, { data: { ...data, sectionId, boardId } });
}

export function updateRecurringChecklistSectionStep(
  id: RecurringChecklistSectionStepId,
  boardId: MarketingBoardId,
  data: Partial<RecurringChecklistSectionStep>,
) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist-step/${id}`, { data: { ...data, boardId } });
}

export function deleteRecurringChecklistSectionStep(id: MarketingBoardId, boardId: MarketingBoardId) {
  return Endpoint.delete<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-checklist-step/${id}`, { data: { boardId } });
}

// setup section
export function createSetupChecklistSection(checklistId: MBMarketingChecklistId, boardId: MarketingBoardId, data: Partial<SetupChecklistSection>) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-checklist-section`, { data: { ...data, checklistId, boardId } });
}

export function updateSetupChecklistSection(id: SetupChecklistSectionId, boardId: MarketingBoardId, data: Partial<SetupChecklistSection>) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-checklist-section/${id}`, { data: { ...data, boardId } });
}

export function deleteSetupChecklistSection(id: SetupChecklistSectionId, boardId: MarketingBoardId) {
  return Endpoint.delete<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-checklist-section/${id}`, { data: { boardId } });
}

// recurring setup section step
export function createSetupChecklistSectionStep(
  sectionId: SetupChecklistSectionId,
  boardId: MarketingBoardId,
  data: Partial<SetupChecklistSectionStep>,
) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-checklist-step`, { data: { ...data, sectionId, boardId } });
}

export function updateSetupChecklistSectionStep(
  id: SetupChecklistSectionStepId,
  boardId: MarketingBoardId,
  data: Partial<SetupChecklistSectionStep>,
) {
  return Endpoint.patch<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-checklist-step/${id}`, { data: { ...data, boardId } });
}

export function deleteSetupChecklistSectionStep(id: MarketingBoardId, boardId: MarketingBoardId) {
  return Endpoint.delete<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-checklist-step/${id}`, { data: { boardId } });
}

export function setupToggleStep(
  boardId: MarketingBoardId,
  checklistId: MBMarketingChecklistId,
  sectionId: SetupChecklistSectionId,
  id: string,
  recurringChecklistId: RecurringChecklistId,
  checked: boolean,
  feedback?: string,
  notes?: string,
) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/setup-toggle-step/${id}`, {
    data: { recurringChecklistId, checked, checklistId, feedback, notes, boardId, sectionId },
  });
}

export function recurringToggleStep(
  boardId: MarketingBoardId,
  checklistId: MBMarketingChecklistId,
  sectionId: SetupChecklistSectionId,
  id: string,
  recurringChecklistId: RecurringChecklistId,
  checked: boolean,
  feedback?: string,
  notes?: string,
) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/recurring-toggle-step/${id}`, {
    data: { recurringChecklistId, checked, feedback, notes, boardId, checklistId, sectionId },
  });
}

export function sortRecurringChecklistSectionSteps(stepIds: string[], boardId: MarketingBoardId) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/sort-recurring-checklist-section-steps`, {
    data: { stepIds, boardId },
  });
}

export function sortSetupChecklistSections(sectionIds: string[], boardId: MarketingBoardId) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/sort-setup-checklist-sections`, {
    data: { sectionIds, boardId },
  });
}

export function sortRecurringChecklistSections(sectionIds: string[], boardId: MarketingBoardId) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/sort-recurring-checklist-sections`, {
    data: { sectionIds, boardId },
  });
}

export function searchBoard(orgId: OrgId, boardId: MarketingBoardId) {
  return Endpoint.get<MarketingBoardResponse>(`@gc/marketing-board-v2/search-board`, {
    params: {
      orgId,
      boardId,
    },
  });
}

export function notOnBoard() {
  return Endpoint.get<{ items: number; orgCount: number }>(`@gc/marketing-board-v2/not-on-board`);
}

export function notOnBoardDetails() {
  return Endpoint.get<NotOnBoardResponse[]>(`@gc/marketing-board-v2/not-on-board-details`);
}

export function order(type: 'setup' | 'recurring', boardId: string, checklistId: string, items: any[]) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/order`, { data: { items, type, boardId, checklistId } });
}

export function updateUsers(boardId: string, type: 'setup' | 'recurring', stepId: string, users: string[]) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/task-assign/${stepId}`, { data: { users, boardId, type } });
}

export function toggleStepNew(boardId: string, type: 'setup' | 'recurring', stepId: string, all?: boolean) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/toggle-step-new/${stepId}`, {
    data: { type, boardId, completeAll: all || false },
  });
}

export function sortSteps(boardId: string, type: 'setup' | 'recurring', stepIds: string[]) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board-v2/sort-steps`, {
    data: { type, stepIds, boardId },
  });
}
